import { useCallback, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ErrorMessage, Field } from '@atlaskit/form';
import UserPicker, {
	isValidEmail,
	type OnChange,
	type UserPickerProps,
	type Value,
} from '@atlaskit/user-picker';

import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { ANALYTICS_PREFIX } from '../../../common/constants.tsx';
import {
	EmailInviteValidationError,
	MAX_ALLOWED_EMAILS,
} from '../hooks/use-email-invites-input.types.ts';
import { extractEmailDomain } from '../utils.ts';

import messages from './messages.tsx';

import type { UseEmailInvitesInput } from '../hooks/use-email-invites-input.types.ts';

const FIELD_NAME = 'onboarding-wizard-invite-emails';
const PICKER_MIN_HEIGHT = 36;

const pickerStyles: NonNullable<UserPickerProps['styles']> = {
	control: (base) =>
		Object.assign({}, base, {
			height: 'auto',
			minHeight: PICKER_MIN_HEIGHT,
		}),
	valueContainer: (base) =>
		Object.assign({}, base, {
			paddingTop: 0,
			paddingBottom: 0,
		}),
	input: (base) =>
		Object.assign({}, base, {
			margin: 0,
			paddingTop: 0,
			paddingBottom: 0,
		}),
};

type EmailInputComponentProps = {
	validatedEmails: UseEmailInvitesInput['validatedEmails'];
	addEmail: UseEmailInvitesInput['addEmail'];
	removeEmail: UseEmailInvitesInput['removeEmail'];
};

const asEmailStrings = (value: Value): string[] => {
	if (!value) {
		return [];
	}
	const options = Array.isArray(value) ? value : [value];
	return options.filter((option) => option.type === 'email').map((option) => option.id);
};

/**
 * In-house email input for the onboarding-wizard invite step. Renders the picker, maps add
 * results to inline errors, and derives the domain suggestion. Email list + validation come
 * from useEmailInvitesInput (owned by the parent) via the validatedEmails/addEmail/removeEmail
 * props.
 *
 * Do NOT use outside of fg: spice-refactor-jira-onboarding-wizard-invites, contact #help-spice for details
 */
export const EmailInputComponent = ({
	validatedEmails,
	addEmail,
	removeEmail,
}: EmailInputComponentProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { email } = useLoggedInUser();
	const emailDomain = extractEmailDomain(email ?? '');

	const [errorState, setErrorState] = useState<EmailInviteValidationError | null>(null);
	const [inputValue, setInputValue] = useState('');
	const pickerValue = useMemo<Value>(
		() =>
			validatedEmails.map((email) => ({
				id: email,
				name: email,
				type: 'email',
			})),
		[validatedEmails],
	);
	// Only suggest "<input>@<domain>" while user hasn't specified a domain or entered invalid characters
	const shouldSuggestDomain = emailDomain !== '' && inputValue !== '' && !inputValue.includes('@');

	const handleInputChange = useCallback((value?: string) => {
		setInputValue(value ?? '');
	}, []);

	const handleFocus = useCallback(() => {
		createAnalyticsEvent({
			action: 'focused',
			actionSubject: 'component',
			actionSubjectId: `${ANALYTICS_PREFIX}InvitePeopleFocus`,
			eventType: 'ui',
		}).fire();
	}, [createAnalyticsEvent]);

	const handleChange = useCallback<OnChange>(
		(value, action) => {
			const next = asEmailStrings(value);

			if (action === 'remove-value' || action === 'clear') {
				validatedEmails
					.filter(
						(email) => !next.some((candidate) => candidate.toLowerCase() === email.toLowerCase()),
					)
					.forEach((email) => removeEmail(email));
				setErrorState(null);
				return;
			}

			const candidate = next.find(
				(email) =>
					!validatedEmails.some((existing) => existing.toLowerCase() === email.toLowerCase()),
			);

			if (!candidate) {
				return;
			}

			const result = addEmail(candidate);
			setErrorState(result.ok ? null : result.error);
		},
		[addEmail, removeEmail, validatedEmails],
	);

	const getErrorMessage = (error: EmailInviteValidationError | null) => {
		switch (error) {
			case EmailInviteValidationError.MAX_ALLOWED_EXCEEDED:
				return formatMessage(messages.emailMaxExceeded, { maxAllowedEmails: MAX_ALLOWED_EMAILS });
			case EmailInviteValidationError.INVALID_FORMAT:
				return formatMessage(messages.emailInvalidFormat);
			default:
				return null;
		}
	};

	const errorMessage = getErrorMessage(errorState);
	const emailShareLabel = formatMessage(messages.emailShare);

	return (
		<div css={fieldStyles}>
			<Field id={FIELD_NAME} name={FIELD_NAME} label={emailShareLabel} isRequired={false}>
				{({ fieldProps }) => (
					<>
						<UserPicker
							fieldId={FIELD_NAME}
							inputId={fieldProps.id}
							ariaLabelledBy={fieldProps['aria-labelledby']}
							value={pickerValue}
							width="100%"
							styles={pickerStyles}
							allowEmail
							isMulti
							suggestEmailsForDomain={shouldSuggestDomain ? emailDomain : undefined}
							noOptionsMessage={() => null}
							isValidEmail={isValidEmail}
							placeholder={formatMessage(messages.emailSharePlaceholder)}
							onChange={handleChange}
							onInputChange={handleInputChange}
							onFocus={handleFocus}
						/>
						{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
					</>
				)}
			</Field>
		</div>
	);
};

const fieldStyles = css({
	width: '100%',
});
